import classNames from 'classnames';
import { Radio, RadioGroup } from '@headlessui/react';

export interface SegmentedOption<T extends string> {
  value: T;
  label: string;
}

interface Props<T extends string> {
  options: SegmentedOption<T>[];
  value: T;
  onChange: (value: T) => void;
  // What the choice is about, for screen readers.
  'aria-label': string;
  className?: string;
}

// A row of two or more options where exactly one is chosen, drawn as a
// pill with the chosen segment raised.
export const SegmentedControl = <T extends string>({
  options,
  value,
  onChange,
  className,
  ...rest
}: Props<T>) => (
  <RadioGroup
    value={value}
    onChange={onChange}
    className={classNames(styles.group, className)}
    aria-label={rest['aria-label']}
  >
    {options.map((option) => (
      <Radio key={option.value} value={option.value} className={styles.segment}>
        {option.label}
      </Radio>
    ))}
  </RadioGroup>
);

const styles = {
  // Tinted, not the field colour: that matches the card in dark mode and
  // the pill would vanish. The raised segment uses the overlay surface, a
  // step lighter in dark and white in light.
  group: 'inline-flex rounded-[10px] bg-ink-secondary/10 p-0.5',
  segment: classNames(
    'cursor-pointer select-none rounded-lg px-3.5 py-1.5 text-sm font-semibold text-ink-secondary',
    'hover:text-ink-primary focus:outline-none',
    'data-checked:bg-surface-overlay data-checked:text-ink-primary data-checked:shadow-xs',
    'data-focus:ring-2 data-focus:ring-accent-ink/40',
  ),
};
